<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_business_point_events')"
                        :isNewButton="checkPermission('coopbusinesspointevent_store')"
                        @new-button-click="openCreateModal()"
                        :isFilter="true"
                        @filter-div-status="datatable.filterStatus = $event" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_business_point_events')"
                              :isNewButton="checkPermission('coopbusinesspointevent_store')"
                              @new-button-click="openCreateModal()"
                              :isFilter="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <!-- Filter -->
            <datatable-filter v-show="datatable.filterStatus"
                               @filter="filter"
                               @filterClear="filterClear"
                               :export-excel="checkPermission('coopbusinesspointevent_excelexport')"
                               @exportExcel="exportExcel()">
                <b-row>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('coop_company')">
                            <coop-company-selectbox v-model="datatable.queryParams.filter.company_id" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('status')">
                            <parameter-selectbox code="coop_business_point_event.statuses" v-model="datatable.queryParams.filter.status" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('faculty')">
                            <faculty-selectbox v-model="datatable.queryParams.filter.faculty_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('department')">
                            <department-selectbox v-model="datatable.queryParams.filter.department_code" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />

            <!-- Create / Edit Modal -->
            <CommonModal ref="formModal" size="lg" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ form.id ? $t('coop_edit_business_point_event') : $t('coop_new_business_point_event') }}
                </template>
                <template v-slot:CommonModalContent>
                    <ValidationObserver ref="formModalValidate">
                        <b-form @submit.prevent="saveEvent">
                            <b-row>
                                <b-col md="6">
                                    <ValidationProvider name="company_id" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_company')">
                                            <coop-company-selectbox v-model="form.company_id" :validate-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="speaker_name" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_speaker_name')">
                                            <b-form-input v-model="form.speaker_name" :state="errors[0] ? false : null" />
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="mb-2"/>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="event_date" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_event_date')">
                                            <SelectDate v-model="form.event_date" :validation-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="3">
                                    <ValidationProvider name="start_time" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_start_time')">
                                            <b-form-input type="time" v-model="form.start_time" :state="errors[0] ? false : null" />
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="mb-2"/>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="3">
                                    <ValidationProvider name="end_time" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_end_time')">
                                            <b-form-input type="time" v-model="form.end_time" :state="errors[0] ? false : null" />
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="mb-2"/>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="slot_duration_minutes" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('coop_slot_duration')">
                                            <b-form-input type="number" v-model.number="form.slot_duration_minutes" min="5" :state="errors[0] ? false : null" />
                                            <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="mb-2"/>
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="status" rules="required" v-slot="{errors}">
                                        <b-form-group :label="$t('status')">
                                            <parameter-selectbox code="coop_business_point_event.statuses" v-model="form.status" :validate-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="faculty_code" rules="" v-slot="{errors}">
                                        <b-form-group :label="$t('faculty')">
                                            <faculty-selectbox v-model="form.faculty_code" :validate-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="department_code" rules="" v-slot="{errors}">
                                        <b-form-group :label="$t('department')">
                                            <department-selectbox v-model="form.department_code" :validate-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <ValidationProvider name="class" rules="" v-slot="{errors}">
                                        <b-form-group :label="$t('class')">
                                            <classes-selectbox v-model="form.class" :validate-error="errors[0]" />
                                        </b-form-group>
                                    </ValidationProvider>
                                </b-col>
                            </b-row>

                            <div class="d-flex justify-content-end mt-3">
                                <b-button type="submit" variant="primary" :disabled="isSubmitting">
                                    {{ $t('save') }}
                                </b-button>
                            </div>
                        </b-form>
                    </ValidationObserver>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>

<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import FacultySelectbox from '@/components/interactive-fields/FacultySelectbox';
    import DepartmentSelectbox from '@/components/interactive-fields/DepartmentSelectbox';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';
    import CoopCompanySelectbox from '@/components/interactive-fields/CoopCompanySelectbox';
    import SelectDate from '@/components/interactive-fields/SelectDate';
    import ClassesSelectbox from '@/components/interactive-fields/ClassesSelectbox';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';

    //Service
    import CoopBusinessPointEventService from '@/services/CoopBusinessPointEventService';

    //Other
    import qs from 'qs';

    export default {
        name: 'BusinessPointEventsIndex',
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable,
            CommonModal,
            FacultySelectbox,
            DepartmentSelectbox,
            ParameterSelectbox,
            CoopCompanySelectbox,
            SelectDate,
            ClassesSelectbox,
            ValidationObserver,
            ValidationProvider
        },
        metaInfo() {
            return {
                title: this.$t('coop_business_point_events')
            };
        },
        data() {
            return {
                isSubmitting: false,
                form: {
                    id: null,
                    company_id: null,
                    speaker_name: '',
                    event_date: '',
                    start_time: '',
                    end_time: '',
                    slot_duration_minutes: 15,
                    faculty_code: null,
                    department_code: null,
                    class: null,
                    status: null
                },
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('edit'),
                                    class: 'ri-edit-line text-primary',
                                    permission: 'coopbusinesspointevent_update',
                                    callback: (row) => {
                                        this.openEditModal(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_participants'),
                                    class: 'ri-group-line text-info',
                                    callback: (row) => {
                                        this.$router.push('/coop/business-point-events/' + row.id + '/participants');
                                    }
                                },
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopbusinesspointevent_delete',
                                    callback: (row) => {
                                        this.deleteEvent(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('company_name'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_speaker_name'),
                            field: 'speaker_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_date'),
                            field: 'event_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_start_time'),
                            field: 'start_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_end_time'),
                            field: 'end_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_slot_duration'),
                            field: 'slot_duration_minutes',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('faculty'),
                            field: this.getLocaleField('faculty_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('department'),
                            field: this.getLocaleField('department_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('class'),
                            field: 'class',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_participants'),
                            field: 'application_count',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: (row) => (row.status === 'active' ? this.$t('active') : row.status),
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getRows();
        },
        methods: {
            getRows() {
                if (!this.checkPermission('coopbusinesspointevent_index')) {
                    return;
                }
                this.datatable.isLoading = true;
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopBusinessPointEventService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                        this.datatable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            exportExcel() {
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopBusinessPointEventService.excelExport(config)
                    .then((response) => {
                        this._downloadFile(response, 'business_point_events.xlsx');
                    })
                    .catch((e) => {
                        this.showErrors(e, null);
                    });
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.filter();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            },
            resetForm() {
                this.form = {
                    id: null,
                    company_id: null,
                    speaker_name: '',
                    event_date: '',
                    start_time: '',
                    end_time: '',
                    slot_duration_minutes: 15,
                    faculty_code: null,
                    department_code: null,
                    class: null,
                    status: null
                };
            },
            openCreateModal() {
                this.resetForm();
                if (this.$refs.formModalValidate) {
                    this.$refs.formModalValidate.reset();
                }
                this.$refs.formModal.$refs.commonModal.show();
            },
            openEditModal(row) {
                this.form = {
                    id: row.id,
                    company_id: row.company_id,
                    speaker_name: row.speaker_name,
                    event_date: row.event_date,
                    start_time: row.start_time ? row.start_time.substring(0, 5) : '',
                    end_time: row.end_time ? row.end_time.substring(0, 5) : '',
                    slot_duration_minutes: row.slot_duration_minutes || 15,
                    faculty_code: row.faculty_code,
                    department_code: row.department_code,
                    class: row.class,
                    status: row.status
                };
                if (this.$refs.formModalValidate) {
                    this.$refs.formModalValidate.reset();
                }
                this.$refs.formModal.$refs.commonModal.show();
            },
            saveEvent() {
                this.$refs.formModalValidate.validate().then((isValid) => {
                    if (!isValid) return;
                    this.isSubmitting = true;
                    const payload = {
                        ...this.form,
                        start_time: this.form.start_time ? this.form.start_time.substring(0, 5) : '',
                        end_time: this.form.end_time ? this.form.end_time.substring(0, 5) : ''
                    };
                    const request = this.form.id
                        ? CoopBusinessPointEventService.update(this.form.id, payload)
                        : CoopBusinessPointEventService.store(payload);

                    request
                        .then((response) => {
                            this.$toast.success(this.$t('api.' + response.data.message));
                            this.$refs.formModal.$refs.commonModal.hide();
                            this.getRows();
                        })
                        .catch((error) => {
                            this.showErrors(error, this.$refs.formModalValidate);
                        })
                        .finally(() => {
                            this.isSubmitting = false;
                        });
                });
            },
            deleteEvent(row) {
                this.$swal.fire({
                    text: this.$t('coop_delete_business_point_event_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopBusinessPointEventService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            }
        }
    };
</script>
